<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>Jobber</page-header>

        <vx-expandable-card>
            <template v-slot:header> Jobber Integration </template>
            <template v-slot:headerRight>
                <div class="flex items-center gap-2">
                    <vx-badge v-if="loading" color="slate">Loading</vx-badge>
                    <vx-badge v-else-if="teamConnection" :color="statusColor(teamConnection.status)">
                        {{ statusLabel(teamConnection.status) }}
                    </vx-badge>
                    <vx-badge v-else-if="locations.length > 0" color="success">
                        {{ connectedLocations.length }}
                        {{ connectedLocations.length === 1 ? 'location' : 'locations' }} connected
                    </vx-badge>
                    <vx-badge v-else color="slate">Not connected</vx-badge>
                </div>
            </template>

            <template v-slot:content>
                <div v-if="!hasPermission" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    Only the team owner can configure integrations.
                </div>
                <div v-else-if="loading" class="flex justify-center py-10">
                    <vx-icon-loading spin class="w-8 h-8 text-sky-600"></vx-icon-loading>
                </div>
                <div v-else-if="teamConnection" class="flex flex-col gap-6 mt-3">
                    <div v-if="teamConnection.status === 'connected'">
                        Your corporate Jobber account {{ teamConnection.vendor_id }} is connected to Voxie.
                    </div>
                    <div v-else>
                        Corporate Jobber account {{ teamConnection.vendor_id }}:
                        {{ statusExplanation(teamConnection.status) }}
                    </div>

                    <div v-if="teamConnection.status === 'connected'">
                        Click the button below to disconnect the corporate Jobber account from Voxie.
                    </div>

                    <div class="flex gap-2">
                        <vx-button
                            v-if="teamConnection.status !== 'connected'"
                            size="lg"
                            :href="initiateUrl()"
                            data-test="reconnect-team"
                        >
                            Reconnect Jobber account
                        </vx-button>
                        <vx-button
                            :color="teamConnection.status === 'connected' ? 'danger' : 'muted-danger'"
                            size="lg"
                            @click="disconnect(teamConnection.vendor_id, 'the corporate Jobber account')"
                            :disabled="disconnecting === teamConnection.vendor_id"
                            data-test="disconnect-team"
                        >
                            {{
                                disconnecting === teamConnection.vendor_id
                                    ? 'Removing...'
                                    : teamConnection.status === 'connected'
                                    ? 'Disconnect Jobber account'
                                    : 'Remove'
                            }}
                        </vx-button>
                    </div>
                </div>
                <div v-else class="flex flex-col gap-6 mt-3">
                    <div>
                        Connect a Jobber account so new clients and requests become contacts in Voxie, and a STOP in
                        Voxie turns texting off for the client in Jobber.
                        <span v-if="locationsAvailable">
                            A franchise connects each location's own Jobber account; a single business connects one
                            corporate account.
                        </span>
                    </div>

                    <div>
                        <vx-button size="lg" @click="openConnect('team')" data-test="connect">
                            Connect Jobber account
                        </vx-button>
                    </div>
                </div>
            </template>
        </vx-expandable-card>

        <vx-expandable-card v-if="hasPermission && !loading && locations.length > 0" data-test="locations-card">
            <template v-slot:header> Connected locations ({{ locations.length }}) </template>
            <template v-slot:headerRight>
                <div class="flex items-center gap-3">
                    <vx-badge v-if="attentionLocations.length > 0" color="orange" data-test="attention">
                        {{ attentionLocations.length }} need{{ attentionLocations.length === 1 ? 's' : '' }} attention
                    </vx-badge>
                    <vx-button size="md" @click="openConnect('location')" data-test="connect-location">
                        Connect location
                    </vx-button>
                </div>
            </template>

            <template v-slot:content>
                <div class="flex flex-col gap-4 mt-3">
                    <vx-input
                        v-if="locations.length > 10 || search"
                        v-model="search"
                        placeholder="Search locations"
                        size="md"
                        data-test="search"
                    />

                    <vx-alert v-if="filteredLocations.length === 0" color="info" :closeable="false">
                        No locations match your search.
                    </vx-alert>

                    <vx-table v-else class="xl:grid-cols-[minmax(0,2fr)_1fr_auto] mt-0" rowBreakpoint="xl">
                        <vx-table-row isHeader>
                            <div>Location</div>
                            <div>Jobber account</div>
                            <div></div>
                        </vx-table-row>

                        <vx-table-row
                            v-for="location in filteredLocations"
                            :key="location.vendor_id"
                            :data-test="`location-${location.group_id}`"
                        >
                            <vx-table-row-title class="wrap-break-word">
                                <div class="flex flex-col gap-1">
                                    <span :title="location.group_name || location.group_id">
                                        {{ location.group_name || location.group_id }}
                                    </span>
                                    <span
                                        v-if="location.group_name"
                                        class="text-xs font-normal text-slate-500 break-all"
                                    >
                                        {{ location.group_id }}
                                    </span>
                                    <span v-else class="text-xs font-normal text-orange-600"
                                        >This group no longer exists.</span
                                    >
                                    <span
                                        v-if="location.status !== 'connected'"
                                        class="text-xs font-normal text-orange-600"
                                        data-test="status"
                                    >
                                        {{ statusNote(location.status) }}
                                    </span>
                                </div>
                            </vx-table-row-title>
                            <vx-table-row-item>
                                <template v-slot:label>Jobber account:</template>
                                <span class="break-all">{{ location.vendor_id }}</span>
                            </vx-table-row-item>
                            <vx-table-row-buttons>
                                <vx-button
                                    v-if="location.status !== 'connected' && location.group_name"
                                    size="sm"
                                    :href="initiateUrl(location.group_id)"
                                    data-test="reconnect-location"
                                >
                                    Reconnect
                                </vx-button>
                                <vx-button
                                    size="sm"
                                    color="muted-danger"
                                    @click="
                                        disconnect(
                                            location.vendor_id,
                                            `the Jobber account of ${location.group_name || location.group_id}`
                                        )
                                    "
                                    :disabled="disconnecting === location.vendor_id"
                                    data-test="disconnect-location"
                                >
                                    {{
                                        disconnecting === location.vendor_id
                                            ? 'Removing...'
                                            : location.status === 'connected'
                                            ? 'Disconnect'
                                            : 'Remove'
                                    }}
                                </vx-button>
                            </vx-table-row-buttons>
                        </vx-table-row>
                    </vx-table>
                </div>
            </template>
        </vx-expandable-card>

        <connect-jobber-account-modal
            v-model:visible="connectModalOpen"
            :teamConnected="!!teamConnection"
            :connectedGroupIds="locations.map((location) => location.group_id)"
            :locationsAvailable="locationsAvailable"
            :initialScope="connectScope"
        />

        <jobber-documentation />

        <integration-logs-card v-if="FEATURES.integration_logs" vendor="jobber" />
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useUrlSearchParams } from '@vueuse/core';
import {
    VxAlert,
    VxBadge,
    VxButton,
    VxExpandableCard,
    VxIconLoading,
    VxInput,
    VxSwal,
    VxTable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { FEATURES } from '~/provides/features';
import PageHeader from '~/components/general/PageHeader.vue';
import IntegrationLogsCard from './IntegrationLogsCard.vue';
import ConnectJobberAccountModal from './jobber/ConnectJobberAccountModal.vue';
import JobberDocumentation from './jobber/JobberDocumentation.vue';
import { jobberInitiateUrl } from './jobber/initiateUrl';
import teamsClient from '~/services/teamsClient';
import { useToasted } from '~/composables/useToasted';
import { throwSwalRequestError } from '~/utils/request';

const props = defineProps({
    ownsTeam: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const queryParams = useUrlSearchParams();
const loading = ref(true);
const teamConnection = ref(null);
const locations = ref([]);
const search = ref('');
const disconnecting = ref(null);
const connectModalOpen = ref(false);
const connectScope = ref('team');

const hasPermission = computed(() => props.ownsTeam);
const locationsAvailable = computed(() => !!FEATURES.contact_groups && !!FEATURES.local_attributes);

const connectedLocations = computed(() => locations.value.filter((location) => location.status === 'connected'));
const attentionLocations = computed(() => locations.value.filter((location) => location.status !== 'connected'));

const filteredLocations = computed(() => {
    const term = search.value.trim().toLowerCase();
    if (!term) {
        return locations.value;
    }

    return locations.value.filter(
        (location) =>
            location.group_id.toLowerCase().includes(term) || (location.group_name || '').toLowerCase().includes(term)
    );
});

const loadConnections = async () => {
    try {
        const response = await teamsClient.getJobberConnections(Spark.state.currentTeam.id);
        teamConnection.value = response.data.team;
        locations.value = response.data.locations;
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        loading.value = false;
    }
};

onMounted(async () => {
    await loadConnections();

    // Like the Connect button, only for a team without a corporate account.
    if (queryParams.connect) {
        queryParams.connect = undefined;
        if (hasPermission.value && !teamConnection.value) {
            openConnect('team');
        }
    }
});

const statusLabel = (status) =>
    ({
        connected: 'Connected',
        needs_reauthorization: 'Expired',
        disconnected: 'Disconnected',
    }[status] ?? status);

const statusColor = (status) => (status === 'connected' ? 'success' : 'orange');

const statusNote = (status) =>
    status === 'disconnected' ? 'Disconnected in Jobber.' : 'Expired. Reconnect to resume.';

const statusExplanation = (status) =>
    status === 'disconnected'
        ? 'The app was disconnected in Jobber. New clients no longer arrive until the account is connected again.'
        : 'Jobber no longer accepts our access to this account. Reconnect it to resume.';

const initiateUrl = (groupId) => jobberInitiateUrl(Spark.state.currentTeam.id, groupId);

const openConnect = (scope) => {
    connectScope.value = scope;
    connectModalOpen.value = true;
};

const disconnect = (vendorId, what) => {
    VxSwal.confirmation(
        'Disconnect Jobber',
        `Are you sure you want to disconnect ${what}? New clients will no longer arrive and STOP replies will no longer reach Jobber.`,
        'danger',
        async () => {
            disconnecting.value = vendorId;
            try {
                await teamsClient.disconnectJobber(Spark.state.currentTeam.id, vendorId);
                if (teamConnection.value?.vendor_id === vendorId) {
                    teamConnection.value = null;
                } else {
                    locations.value = locations.value.filter((location) => location.vendor_id !== vendorId);
                }
                toasted.success('Jobber account disconnected successfully.');
            } catch (error) {
                throwSwalRequestError(error);
            } finally {
                disconnecting.value = null;
            }
        }
    );
};
</script>
